import { useState, useEffect } from 'react'
import Button from '@mui/material/Button'
import { useSessionStore } from '../../state'
import { canCancel } from '../../utils/cancellationEligibility'
import { getTransactionByJoinRequest } from '../../services/paymentService'
import {
  cancelWithCredit,
  cancelWithMonetaryRefund,
  cancelBuddyBooking
} from '../../services/cancellationService'
import CancellationDialog from '../CancellationDialog'

export type CancelBookingButtonProps = {
  joinRequestId: string
  joinRequestStatus: string
  paymentStatus: string | null
  requesterId: string
  diveId: string
  diveStatus: string
  diveDateTime?: Date
  onSuccess?: () => void
}

const CancelBookingButton = ({
  joinRequestId,
  joinRequestStatus,
  paymentStatus,
  requesterId,
  diveId,
  diveStatus,
  diveDateTime,
  onSuccess
}: CancelBookingButtonProps) => {
  const user = useSessionStore(s => s.user)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [transactionAmount, setTransactionAmount] = useState(0)
  const [stripeFee, setStripeFee] = useState(0)

  const eligibility = canCancel(
    { status: joinRequestStatus, paymentStatus, requesterId },
    diveStatus,
    user?.uid ?? null,
    diveDateTime
  )

  const isBuddyDive = eligibility.eligible && eligibility.refundType === 'buddy'

  // Fetch transaction data when dialog opens for guided dives
  useEffect(() => {
    if (!dialogOpen || isBuddyDive) return

    let cancelled = false

    const fetchTransaction = async () => {
      try {
        const tx = await getTransactionByJoinRequest(joinRequestId, diveId)
        if (!cancelled && tx) {
          setTransactionAmount(tx.amount)
          setStripeFee(tx.stripeFee ?? 0)
        }
      } catch {
        // Transaction data will default to 0 — dialog can still show
      }
    }

    fetchTransaction()
    return () => {
      cancelled = true
    }
  }, [dialogOpen, isBuddyDive, joinRequestId, diveId])

  if (!eligibility.eligible) {
    return null
  }

  const handleClick = () => {
    setDialogOpen(true)
  }

  const handleClose = () => {
    if (!isProcessing) {
      setDialogOpen(false)
    }
  }

  const handleConfirm = async (refundType: 'credit' | 'monetary') => {
    setIsProcessing(true)

    try {
      let result

      if (isBuddyDive) {
        result = await cancelBuddyBooking(joinRequestId)
      } else if (refundType === 'credit') {
        result = await cancelWithCredit(joinRequestId)
      } else {
        result = await cancelWithMonetaryRefund(joinRequestId)
      }

      if (result.success) {
        setDialogOpen(false)
        onSuccess?.()
      }
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <>
      <Button variant='outlined' color='error' onClick={handleClick}>
        Cancel Booking
      </Button>
      <CancellationDialog
        open={dialogOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        joinRequestId={joinRequestId}
        transactionAmount={transactionAmount}
        stripeFee={stripeFee}
        isBuddyDive={isBuddyDive}
        isProcessing={isProcessing}
      />
    </>
  )
}

export default CancelBookingButton
